import { useCallback, useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { useDispatch } from 'react-redux';

import getPhotoManagerSubscriptionItems from '../../../../../graphql/queries/getPhotoManagerSubscriptionItems';
import { AccountPhotoManagerSubscriptions } from '../../../../../graphql/queries/account';
import { checkoutPhotoManagerSubscription as checkoutPhotoManagerSubscriptionMutation } from '../../../../../graphql/mutations/checkout';
import { openAlert } from '../../../../../actions/modal-actions';
import { getErrorString } from '../../../../../util/normalize-errors';
import { gtm } from '../../../../../util/analytics';
import { formatPriceForDisplay } from '../../../../../util/format-money';

/**
 * PhotoManagerSubscription checkout hook.
 *
 * @param {Object} options The options.
 * @param {function} [options.onCheckoutSuccess] Checkout success callback, fired on successful checkout.
 * @returns {{onCheckout: (function({photoManagerSubscriptionItemId?: *, stripeToken?: *}): Promise<undefined>), checkingOut: boolean, photoManagerSubscriptionItems, account}}
 */
const usePhotoManagerSubscriptionsCheckout = ({ onCheckoutSuccess }) => {
  const [checkingOut, setCheckingOut] = useState(false);
  const [promoCode, setPromoCode] = useState(null);
  const [accountPromoCode, setAccountPromoCode] = useState(null);
  const { data: { me } = {}, refetch: refetchAccount } = useQuery(
    AccountPhotoManagerSubscriptions,
    {
      options: {
        fetchPolicy: 'network-only',
      },
    },
  );
  const { data: { photoManagerSubscriptionItems } = {} } = useQuery(
    getPhotoManagerSubscriptionItems,
  );
  const [checkout] = useMutation(checkoutPhotoManagerSubscriptionMutation);
  const dispatch = useDispatch();
  const onErrorModalOpen = useCallback(state => dispatch(openAlert(state)), [
    dispatch,
  ]);

  const onCheckout = async ({
    photoManagerSubscriptionItemId,
    stripeToken,
  }) => {
    if (checkingOut) {
      return;
    }

    const selectedSubscriptionItem = photoManagerSubscriptionItems?.find(
      item => item.id === photoManagerSubscriptionItemId,
    );

    setCheckingOut(true);
    try {
      const {
        data: { checkoutPhotoManagerSubscription } = {},
      } = await checkout({
        variables: {
          input: {
            photo_manager_subscription_item_id: photoManagerSubscriptionItemId,
            email_address: stripeToken?.email ?? me?.email_address,
            stripe_token: stripeToken?.id,
            promo_code: promoCode,
          },
        },
      });
      if (checkoutPhotoManagerSubscription?.success) {
        await refetchAccount?.();
        await onCheckoutSuccess?.();

        gtm.trackCustomEvent({
          event: 'SubscriptionPurchase',
          package: `PM_${selectedSubscriptionItem?.billing_period}`,
          value: formatPriceForDisplay(selectedSubscriptionItem?.price?.value),
          user_id: me?.id,
        });

        // Legacy -- Should be removed after 5/15/2022
        gtm.trackCustomEvent({
          event: 'ProPurchase',
          package: `PM_${selectedSubscriptionItem?.billing_period}`,
          value: formatPriceForDisplay(selectedSubscriptionItem?.price?.value),
          user_id: me?.id,
        });
      } else {
        onErrorModalOpen({
          message: 'There was an error during checkout, please try again.',
        });
      }
    } catch (error) {
      onErrorModalOpen({
        message: 'There was an error during checkout, please try again.',
        info: error?.request_id || getErrorString(error),
      });
    } finally {
      setCheckingOut(false);
    }
  };

  return {
    checkingOut,
    account: me,
    photoManagerSubscriptionItems,
    onCheckout,
    promoCode,
    setPromoCode,
    accountPromoCode,
    setAccountPromoCode,
  };
};

export default usePhotoManagerSubscriptionsCheckout;
